import { gsap } from "gsap";

export function registerEventSectionTransitionHandler() {
  // Track if elements have been animated
  let elementsAnimated = false;

  // Check if we're on a small screen
  const isSmallScreen = () => window.innerWidth < 768;

  // Observer to watch for the event section becoming visible
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !elementsAnimated) {
          initializeEventSection();
          elementsAnimated = true;

          // For large screens, we want to keep observing to re-trigger animations
          // when scrolling back to this section
          if (!isSmallScreen()) {
            observer.disconnect(); // Remove the first observer
            setupLargeScreenScrollObserver(); // Add new observer for re-entries
          }
        }
      });
    },
    { threshold: 0.2 } // Trigger when 20% of the event section is visible
  );

  // Start observing the event section
  const eventSection = document.querySelector("#event");
  if (eventSection) {
    observer.observe(eventSection);
  }

  // Setup a persistent observer for large screens to trigger animations on scroll
  function setupLargeScreenScrollObserver() {
    if (isSmallScreen()) return; // Only for large screens

    const scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Only animate if we're on large screen
            if (!isSmallScreen()) {
              animateLargeScreenElements();
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    if (eventSection) {
      scrollObserver.observe(eventSection);
    }
  }

  // Handle window resize
  window.addEventListener("resize", () => {
    if (elementsAnimated) {
      // If elements are already animated, adjust layout based on new screen size
      const generalInfo = document.querySelector(".general-information");
      const timeline = document.querySelector(".timeline");

      if (!generalInfo || !timeline) return;

      if (isSmallScreen()) {
        // Small screen: hide timeline, show generalInfo
        gsap.set(generalInfo, { display: "flex", autoAlpha: 1 });
        gsap.set(timeline, { display: "none", autoAlpha: 0 });
      } else {
        // Large screen: show both
        gsap.set(generalInfo, { display: "flex", autoAlpha: 1 });
        gsap.set(timeline, { display: "flex", autoAlpha: 1 });
      }
    }
  });

  function initializeEventSection() {
    const generalInfo = document.querySelector(".general-information");
    const timeline = document.querySelector(".timeline");

    if (!generalInfo || !timeline) return;

    if (isSmallScreen()) {
      // SMALL SCREEN BEHAVIOR
      // Initially show general info and hide timeline
      gsap.set(generalInfo, { autoAlpha: 0, y: 50 });
      gsap.set(timeline, { autoAlpha: 0, display: "none" });

      // Animate general info in
      animateGeneralInfoIn();

      // Set up click handlers for toggling
      setupMobileClickHandlers(generalInfo, timeline);
    } else {
      // LARGE SCREEN BEHAVIOR - Initial setup
      animateLargeScreenElements();
    }
  }

  function animateLargeScreenElements() {
    const generalInfo = document.querySelector(".general-information");
    const timeline = document.querySelector(".timeline");

    if (!generalInfo || !timeline) return;

    // Reset both elements to their initial state for animation
    gsap.set([generalInfo, timeline], { autoAlpha: 0, y: 50 });

    // Create a timeline for sequential animations
    const tl = gsap.timeline();

    // First animate general info
    tl.to(generalInfo, {
      autoAlpha: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out",
    });

    // Then animate the timeline elements
    tl.add(() => {
      // Show the timeline container first
      gsap.set(timeline, { display: "flex", autoAlpha: 0, y: 30 });
      gsap.to(timeline, {
        autoAlpha: 1,
        y: 0,
        duration: 0.1,
        ease: "power3.out",
        onComplete: animateTimelineElements,
      });
    }, "+=0.1");
  }

  function animateGeneralInfoIn() {
    const generalInfo = document.querySelector(".general-information");
    if (!generalInfo) return;

    gsap.to(generalInfo, {
      autoAlpha: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out",
      onComplete: () => {
        // Add a subtle pulsing effect to draw attention
        gsap.to(generalInfo, {
          boxShadow: "0 0 15px rgba(255,255,255,0.5)",
          duration: 1.2,
          repeat: 2,
          yoyo: true,
          ease: "sine.inOut",
        });
      },
    });
  }

  function setupMobileClickHandlers(generalInfo, timeline) {
    // Make elements clickable
    generalInfo.style.cursor = "pointer";
    timeline.style.cursor = "pointer";

    // Add visual indicator for mobile
    const clickPrompt = document.createElement("div");
    clickPrompt.innerHTML = "<i>Tap to view timeline</i>";
    clickPrompt.style.cssText =
      "text-align:center; margin-top:15px; color:rgba(0,0,0,0.6); font-size:14px;";
    const card = generalInfo.querySelector(".general-information__card");
    card.appendChild(clickPrompt);

    // Add hover effects for desktop (optional on mobile)
    addHoverEffects(generalInfo, timeline);

    // Click handlers
    generalInfo.addEventListener("click", () =>
      showElement(timeline, generalInfo)
    );
    timeline.addEventListener("click", () =>
      showElement(generalInfo, timeline)
    );
  }

  function addHoverEffects(generalInfo, timeline) {
    // Hover effect for general info
    generalInfo.addEventListener("mouseenter", () => {
      gsap.to(generalInfo, {
        scale: 1.03,
        boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
        duration: 0.3,
        ease: "power2.out",
      });
    });

    generalInfo.addEventListener("mouseleave", () => {
      gsap.to(generalInfo, {
        scale: 1,
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        duration: 0.3,
        ease: "power2.out",
      });
    });

    // Hover effect for timeline
    timeline.addEventListener("mouseenter", () => {
      gsap.to(timeline, {
        scale: 1.02,
        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        duration: 0.3,
        ease: "power2.out",
      });
    });

    timeline.addEventListener("mouseleave", () => {
      gsap.to(timeline, {
        scale: 1,
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        duration: 0.3,
        ease: "power2.out",
      });
    });
  }

  function showElement(elementToShow, elementToHide) {
    const tl = gsap.timeline();

    // Hide current element
    tl.to(elementToHide, {
      y: -40,
      autoAlpha: 0,
      scale: 0.9,
      duration: 0.5,
      ease: "power2.inOut",
    });

    // Prepare next element
    tl.set(elementToShow, {
      display: "flex",
      autoAlpha: 0,
      y: 30,
    })
      .set(elementToHide, { display: "none" })
      .to(elementToShow, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.7,
        ease: "power3.out",
        onComplete: () => {
          // If showing timeline, animate its elements
          if (elementToShow.classList.contains("timeline")) {
            animateTimelineElements();
          }
        },
      });
  }

  function animateTimelineElements() {
    const steps = document.querySelectorAll(".timeline__step");
    const verticalLine = document.querySelector(".timeline__vertical-line");

    // Create a timeline for sequential animations
    const tl = gsap.timeline();

    // Animate the vertical line
    if (verticalLine) {
      tl.fromTo(
        verticalLine,
        { height: 0 },
        {
          height: "100%",
          visibility: "visible",
          duration: 0.7,
          ease: "power2.inOut",
        }
      );
    }

    // Animate steps with staggered timing
    tl.fromTo(
      steps,
      { scale: 0.9, autoAlpha: 0 },
      {
        scale: 1,
        autoAlpha: 1,
        duration: 0.5,
        stagger: 0.15,
        ease: "back.out(1.7)",
      },
      "-=0.3"
    );

    // Animate connectors
    const connectors = document.querySelectorAll(".timeline__step__connector");
    tl.fromTo(
      connectors,
      { width: 0 },
      {
        width: "100%",
        duration: 0.8,
        stagger: 0.15,
        ease: "power1.inOut",
      },
      "-=0.6"
    );
  }
}

export function registerCountdownAnimation(weddingDate) {
  // Get the countdown elements
  const daysEl = document.getElementById("days");
  const hoursEl = document.getElementById("hours");
  const minutesEl = document.getElementById("minutes");
  const secondsEl = document.getElementById("seconds");

  // Check if elements exist
  if (!daysEl || !hoursEl || !minutesEl || !secondsEl) return;

  // Define time units and their values in milliseconds
  const timeUnits = {
    days: 24 * 60 * 60 * 1000,
    hours: 60 * 60 * 1000,
    minutes: 60 * 1000,
    seconds: 1000,
  };

  // Map unit names to DOM elements
  const unitElements = {
    days: daysEl,
    hours: hoursEl,
    minutes: minutesEl,
    seconds: secondsEl,
  };

  // Function to update the countdown
  function updateCountdown() {
    // Get current time
    const now = new Date();

    // Calculate time difference
    let diff = weddingDate - now;

    // If the wedding date has passed, show zeros
    if (diff < 0) {
      diff = 0;
    }

    // Calculate and update each unit
    let remainingTime = diff;

    for (const [unit, unitMs] of Object.entries(timeUnits)) {
      const value = Math.floor(remainingTime / unitMs);
      remainingTime %= unitMs;

      // Update the DOM with new values, adding leading zeros
      const element = unitElements[unit];
      if (element) {
        // Add animation class if the value changes
        const currentValue = element.textContent;
        const newValue = String(value).padStart(2, "0");

        if (currentValue !== newValue) {
          // Add pulse animation class
          element.classList.add("countdown__number--changing");

          // Set the new value
          element.textContent = newValue;

          // Remove animation class after animation completes
          setTimeout(() => {
            element.classList.remove("countdown__number--changing");
          }, 400);
        }
      }
    }
  }

  // Initial update
  updateCountdown();

  // Update every second
  const countdownInterval = setInterval(updateCountdown, 1000);

  // Clean up interval when page changes (optional)
  window.addEventListener("beforeunload", () => {
    clearInterval(countdownInterval);
  });

  return countdownInterval;
}

//#region Load Event Information

export function loadEventInformation() {
  const events = getEvents();

  // Parse URL to get event ID
  const eventId = parseEventIdFromUrl() || "bao_hy"; // Default to bao_hy if not specified

  // Find the event data
  const eventData = events.find((event) => event.id === eventId);

  if (!eventData) {
    console.error(`Event with ID '${eventId}' not found.`);
    return;
  }

  // Update general information
  updateGeneralInformation(eventData);

  // Update timeline
  updateTimeline(eventData.timeline);

  const weddingDate = getWeddingDate(eventData);
  registerCountdownAnimation(weddingDate);
}

function getEvents() {
  const iconImageSrcs = Array.from(
    document.querySelectorAll(".timeline__step__content img")
  ).map((el) => el.getAttribute("src"));
  const iconUrls = {
    rings: iconImageSrcs[0],
    guests: iconImageSrcs[2],
    party: iconImageSrcs[1],
    thanks: iconImageSrcs[3],
  };
  const events = [
    {
      id: "vu_quy",
      name: "Lễ Vu Quy",
      date: "19.07.2025",
      weekday: "Thứ 7",
      placeName: "Tư Gia Nhà Gái",
      placeAddress: "Ia Phang, Chư Pưh, Gia Lai",
      timeline: [
        {
          name: "Làm Lễ",
          iconUrl: iconUrls.rings,
          time: "09:00",
        },
        {
          name: "Đón Khách",
          iconUrl: iconUrls.guests,
          time: "10:30",
        },
        {
          name: "Khai Tiệc",
          iconUrl: iconUrls.party,
          time: "11:00",
        },
        {
          name: "Cảm Ơn",
          iconUrl: iconUrls.thanks,
          time: "14:00",
        },
      ],
    },
    {
      id: "tan_hon",
      name: "Lễ Tân Hôn",
      date: "20.07.2025",
      weekday: "Chủ nhật",
      placeName: "Tư Gia Nhà Trai",
      placeAddress: "Ia Dreng, Chư Pưh, Gia Lai",
      timeline: [
        {
          name: "Làm Lễ",
          iconUrl: iconUrls.rings,
          time: "09:00",
        },
        {
          name: "Đón Khách",
          iconUrl: iconUrls.guests,
          time: "10:30",
        },
        {
          name: "Khai Tiệc",
          iconUrl: iconUrls.party,
          time: "11:00",
        },
        {
          name: "Cảm Ơn",
          iconUrl: iconUrls.thanks,
          time: "14:00",
        },
      ],
    },
    {
      id: "bao_hy",
      name: "Lễ Báo Hỷ",
      date: "26.07.2025",
      weekday: "Thứ 7",
      placeName: "Gala Royale, Tầng 5",
      placeAddress: "63 Mạc Đĩnh Chi, Quận 1, TP. HCM",
      timeline: [
        {
          name: "Đón Khách",
          iconUrl: iconUrls.guests,
          time: "10:30",
        },
        {
          name: "Làm Lễ",
          iconUrl: iconUrls.rings,
          time: "11:00",
        },
        {
          name: "Khai Tiệc",
          iconUrl: iconUrls.party,
          time: "11:30",
        },
        {
          name: "Cảm Ơn",
          iconUrl: iconUrls.thanks,
          time: "14:00",
        },
      ],
    },
  ];

  return events;
}

function parseEventIdFromUrl() {
  // Extract event ID from URL path or query parameters
  const urlPath = window.location.pathname.split("/").filter(Boolean).pop();

  // Pattern matching for vu-quy, tan-hon, bao-hy
  if (urlPath) {
    if (urlPath.includes("vu_quy") || urlPath.includes("vu-quy"))
      return "vu_quy";
    if (urlPath.includes("tan_hon") || urlPath.includes("tan-hon"))
      return "tan_hon";
    if (urlPath.includes("bao_hy") || urlPath.includes("bao-hy"))
      return "bao_hy";
  }

  // Also check URL parameters (e.g. ?event=vu_quy)
  const urlParams = new URLSearchParams(window.location.search);
  const eventParam = urlParams.get("event");

  if (eventParam) {
    // Convert hyphenated to underscore if necessary
    return eventParam.replace("-", "_");
  }

  return null; // No event ID found in URL
}

function updateGeneralInformation(eventData) {
  // Select elements to update
  const titleEl = document.querySelector(".general-information__event-title");
  const timeEl = document.querySelector(".general-information__time");
  const placeNameEl = document.querySelector(
    ".general-information__place-name"
  );
  const addressEl = document.querySelector(".general-information__address");
  const saveTheDateEl = document.querySelector(".save-the-dates .date");

  // Update elements if they exist
  if (titleEl) titleEl.textContent = eventData.name;
  if (timeEl) timeEl.textContent = `${eventData.weekday} - ${eventData.date}`;
  if (placeNameEl) placeNameEl.textContent = eventData.placeName;
  if (addressEl) addressEl.textContent = eventData.placeAddress;
  if (saveTheDateEl) saveTheDateEl.textContent = eventData.date;
  // Also update the document title to include the event name
  document.title = `Anh Tuấn & Thanh Sương | ${eventData.name}`;
}

function updateTimeline(timelineData) {
  const timelineEl = document.querySelector(".timeline");
  if (!timelineEl) {
    console.warn("Timeline element not found in the DOM.");
    return;
  }

  const timeLineStepEls = Array.from(
    timelineEl.querySelectorAll(".timeline__step")
  );
  const orderedStepEls = [
    timeLineStepEls[0],
    timeLineStepEls[2],
    timeLineStepEls[1],
    timeLineStepEls[3],
  ];

  orderedStepEls.forEach((stepEl, index) => {
    // Set the icon
    const iconEl = stepEl.querySelector("img");
    if (iconEl) {
      const iconUrl = timelineData[index].iconUrl || ringsIcon; // Default to rings icon if not specified
      iconEl.src = iconUrl;
    }

    // Set the time
    const timeEl = stepEl.querySelector(".timeline__step__time");
    if (timeEl) {
      timeEl.textContent = timelineData[index].time;
    }

    // Set the name
    const nameEl = stepEl.querySelector(".timeline__step__title");
    if (nameEl) {
      nameEl.textContent = timelineData[index].name;
    }
  });
}

function getWeddingDate(eventData) {
  const dateParts = eventData.date.split(".");
  const timeParts = eventData.timeline[0].time.split(":");

  return new Date(
    parseInt(dateParts[2]), // Year
    parseInt(dateParts[1]) - 1, // Month (0-based)
    parseInt(dateParts[0]), // Day
    parseInt(timeParts[0]), // Hours
    parseInt(timeParts[1]), // Minutes
    0
  );
}
